import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  CpuChipIcon,
  PencilSquareIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  XMarkIcon,
  PlusCircleIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net';

// const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/';
// Componente Modal genérico
const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur">
      <Card className="relative w-full max-w-2xl">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="absolute right-3 top-3"
        >
          <XMarkIcon className="h-5 w-5" />
        </Button>
        <CardContent className="max-h-[90vh] overflow-y-auto pt-6">{children}</CardContent>
      </Card>
    </div>
  );
};
// Botão de upload de arquivo (usado no modal de arquivos)
const FileUploadButton = ({ isUploading, onUpload }) => {
  const inputRef = useRef(null);
  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file && onUpload) {
      onUpload(file);
      e.target.value = ''; // reseta para permitir o mesmo arquivo depois
    }
  };
  return (
    <div>
      <Button type="button" disabled={isUploading} onClick={() => inputRef.current?.click()}>
        {isUploading ? 'Enviando...' : 'Escolher arquivo'}
      </Button>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={handleChange}
        disabled={isUploading}
      />
    </div>
  );
};
// Componente para o formulário de criação/edição de assistentes
const AssistantForm = ({
  assistant,
  onSave,
  onSaveAndNavigate,
  onCancel,
  isSubmitting,
  selectedTools = [],
  onOpenToolsModal,
  tokenClient,
  selectedFiles = [],
  onOpenFilesModal,
  files = [],
  setSelectedFiles,
  isUploading,
  onUpload,
}) => {
  const isEditing = !!assistant;
  const buildStableKey = (prefix, idValue, index) => {
    if (idValue !== undefined && idValue !== null && String(idValue).trim() !== '') {
      return `${prefix}-${String(idValue)}`;
    }
    return `${prefix}-idx-${index}`;
  };
  const processValue = (value, type) => {
    if (typeof value === 'undefined' || value === null || value === '') {
      return type === 'number' ? 0 : '';
    }
    if (type === 'number') {
      const num = Number(value);
      return isNaN(num) ? 0 : num;
    }
    return value;
  };
  const getInitialFormData = () => {
    const defaultValues = {
      nome: '',
      prompt: '',
      firstMessage: '',
      voiceProvider: 'elevenlabs',
      voiceId: '33B4UnXyTNbgLmdEDh5P',
      voiceModel: 'eleven_multilingual_v2',
      voiceSpeed: 1,
      voiceStability: 0.5,
      voiceSimilarityBoost: 0.8,
      voiceStyle: 0.2,
      backgroundSound: 'office',
      maxDurationSeconds: 600,
      endCallMessage: '',
      idleTimeoutSeconds: 10,
      silenceTimeoutSeconds: 10,
      triggerMaxCount: 3,
      triggerResetMode: 'onUserSpeech',
      idleMessages: [
        "Você ainda está aí?",
        "Posso ajudar com mais alguma coisa?",
        "Estou aqui quando você estiver pronto para continuar."
      ],
      firstMessageMode: 'assistant-speaks-first', // Adicionando o novo campo com valor padrão
      keypadInputPlanEnabled: false,
      keypadInputPlanTimeoutSeconds: 5,
      keypadInputPlanDelimiters: ['#'], // Array de delimitadores selecionados

    };
    if (!assistant) {
      const { idleTimeoutSeconds, triggerMaxCount, triggerResetMode, idleMessages, ...creationDefaults } = defaultValues;
      return creationDefaults;
    }
    const voiceConfig = assistant.config?.voice || {};
    const backgroundSoundConfig = assistant.config?.backgroundSound || assistant.backgroundSound || 'office';
    const idleHook = assistant.config?.hooks?.find(hook => hook.name === "idle_message_check");
    const formDataResult = {
      nome: assistant.nome || assistant.name || '',
      prompt: assistant.prompt ||
        assistant.model?.messages?.[0]?.content ||
        assistant.config?.model?.messages?.[0]?.content ||
        '',
      firstMessage: assistant.firstMessage || '',
      voiceProvider: 'elevenlabs',
      voiceId: assistant.voiceId ||
        assistant.config?.voice?.voiceId ||
        '33B4UnXyTNbgLmdEDh5P',
      voiceModel: voiceConfig.model || 'eleven_multilingual_v2',
      voiceSpeed: processValue(
        voiceConfig.speed !== undefined ? voiceConfig.speed :
          assistant.voiceSpeed !== undefined ? assistant.voiceSpeed :
            1,
        'number'
      ),
      voiceStability: processValue(voiceConfig.stability !== undefined ? voiceConfig.stability : 0.5, 'number'),
      voiceSimilarityBoost: processValue(voiceConfig.similarityBoost !== undefined ? voiceConfig.similarityBoost : 0.8, 'number'),
      voiceStyle: processValue(
        voiceConfig.style !== undefined ? voiceConfig.style :
          assistant.voiceStyle !== undefined ? assistant.voiceStyle :
            0.2,
        'number'
      ),
      backgroundSound: backgroundSoundConfig,
      maxDurationSeconds: processValue(
        assistant.maxDurationSeconds !== undefined ? assistant.maxDurationSeconds :
          assistant.config?.maxDurationSeconds !== undefined ? assistant.config.maxDurationSeconds :
            assistant.config?.model?.maxDurationSeconds !== undefined ? assistant.config.model.maxDurationSeconds :
              600, 'number'
      ),
      endCallMessage: assistant.endCallMessage ||
        assistant.config?.endCallMessage ||
        '',
      idleTimeoutSeconds: processValue(
        idleHook?.options?.timeoutSeconds !== undefined ? idleHook.options.timeoutSeconds :
          assistant.idleTimeoutSeconds !== undefined ? assistant.idleTimeoutSeconds :
            10, 'number'
      ),
      silenceTimeoutSeconds: processValue(
        assistant.config?.silenceTimeoutSeconds !== undefined ? assistant.config.silenceTimeoutSeconds :
          assistant.silenceTimeoutSeconds !== undefined ? assistant.silenceTimeoutSeconds :
            10, 'number'
      ),
      triggerMaxCount: processValue(
        idleHook?.options?.triggerMaxCount !== undefined ? idleHook.options.triggerMaxCount :
          assistant.triggerMaxCount !== undefined ? assistant.triggerMaxCount :
            3, 'number'
      ),
      triggerResetMode:
        idleHook?.options?.triggerResetMode ||
        assistant.triggerResetMode ||
        'onUserSpeech',
      idleMessages:
        idleHook?.do?.[0]?.exact ||
        assistant.idleMessages ||
        [
          "Você ainda está aí?",
          "Posso ajudar com mais alguma coisa?",
          "Estou aqui quando você estiver pronto para continuar."
        ],
      firstMessageMode: assistant.firstMessageMode !== undefined ? assistant.firstMessageMode : 'assistant-speaks-first',// Adicionando o novo campo com valor padrão
      // Carregar keypadInputPlan do assistente
      keypadInputPlanEnabled: assistant.config?.keypadInputPlan?.enabled !== undefined
        ? assistant.config.keypadInputPlan.enabled
        : false,
      keypadInputPlanTimeoutSeconds: processValue(
        assistant.config?.keypadInputPlan?.timeoutSeconds !== undefined
          ? assistant.config.keypadInputPlan.timeoutSeconds
          : 5,
        'number'
      ),
      keypadInputPlanDelimiters: Array.isArray(assistant.config?.keypadInputPlan?.delimiters) && assistant.config.keypadInputPlan.delimiters.length > 0
        ? assistant.config.keypadInputPlan.delimiters
        : ['#'],
    };

    return formDataResult;
  };
  const [formData, setFormData] = useState(getInitialFormData());
  const [showFileDropdown, setShowFileDropdown] = useState(false);
  const [fileSearch, setFileSearch] = useState("");
  const [structuredOutputs, setStructuredOutputs] = useState([]);
  const [isStructuredOutputsExpanded, setIsStructuredOutputsExpanded] = useState(false);
  const [isIdleMessagesExpanded, setIsIdleMessagesExpanded] = useState(false);
  const [isKeypadInputExpanded, setIsKeypadInputExpanded] = useState(false);
  const [isVoiceAdvancedExpanded, setIsVoiceAdvancedExpanded] = useState(false);
  const navigate = useNavigate();
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const clienteId = userData.clienteId || userData._id || userData.id;
  const isAdmin = userData.role === 'admin';
  
  // Lista filtrada dos arquivos baseado no campo de busca
  const filteredFiles = Array.isArray(files)
    ? files.filter(f =>
      (f.originalName || f.name || "")
        .toLowerCase()
        .includes(fileSearch.toLowerCase())
    )
    : [];

  const getAuthConfig = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchStructuredOutputsForAssistant = async (assistantId) => {
    if (!assistantId) {
      setStructuredOutputs([]);
      return;
    }
    try {
      const config = getAuthConfig();
      if (!config) return;
      
      // const structuredOutputsBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
      const structuredOutputsBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net';
      const params = {};
      if (!isAdmin && clienteId) {
        params.clienteId = clienteId;
      }
      const response = await axios.get(`${structuredOutputsBaseUrl}/api/structured-outputs`, {
        ...config,
        params
      });
      
      const outputs = Array.isArray(response.data) ? response.data : [];
      const assistantIdStr = String(assistantId);
      const linkedOutputs = outputs.filter(output => {
        const assistantIds = output.assistantIds || [];
        return assistantIds.some(id => String(id) === assistantIdStr);
      });
      
      setStructuredOutputs(linkedOutputs);
    } catch (err) {
      console.error('Erro ao buscar estruturas de saída:', err.response?.data || err.message);
      setStructuredOutputs([]);
    }
  };

  useEffect(() => {
    if (assistant) {
      setFormData(getInitialFormData());
      // Buscar estruturas de saída vinculadas ao assistente
      const assistantId = assistant.vapiId || assistant.id;
      if (assistantId) {
        fetchStructuredOutputsForAssistant(assistantId);
      }
    } else {
      setFormData({
        nome: '',
        prompt: '',
        firstMessage: '',
        voiceProvider: 'elevenlabs',
        voiceId: '33B4UnXyTNbgLmdEDh5P',
        voiceModel: 'eleven_multilingual_v2',
        voiceSpeed: 1,
        voiceStability: 0.5,
        voiceSimilarityBoost: 0.8,
        voiceStyle: 0.2,
        maxDurationSeconds: 600,
        endCallMessage: '',
      });
      setStructuredOutputs([]);
    }
  }, [assistant]);
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const numericFields = [
      'idleTimeoutSeconds',
      'silenceTimeoutSeconds',
      'triggerMaxCount',
      'maxDurationSeconds',
      'keypadInputPlanTimeoutSeconds',
      'voiceSpeed',
      'voiceStability',
      'voiceSimilarityBoost',
      'voiceStyle'
    ];
    if (numericFields.includes(name)) {
      // Permitir digitação livre - aceitar o valor diretamente do input
      // O campo number do HTML já valida o formato básico
      if (value === '') {
        setFormData(prev => ({ ...prev, [name]: '' }));
      } else {
        const numValue = Number(value);
        // Aceitar o valor se for um número válido, caso contrário manter o anterior
        if (!isNaN(numValue) && isFinite(numValue)) {
          setFormData(prev => ({
            ...prev,
            [name]: numValue
          }));
        }
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };
  const handleSliderChange = (e) => {
    const { name, value } = e.target;
    const numValue = Number(value);
    setFormData(prev => ({
      ...prev,
      [name]: isNaN(numValue) ? 0 : numValue
    }));
  };
  const buildDataToSend = () => {
    // Validar keypadInputPlan se estiver habilitado
    if (isEditing && formData.keypadInputPlanEnabled) {
      const delimiters = Array.isArray(formData.keypadInputPlanDelimiters) ? formData.keypadInputPlanDelimiters : [];
      if (delimiters.length === 0) {
        alert('Por favor, selecione pelo menos um delimitador (# ou *) para a entrada por teclado.');
        return null;
      }
    }

    return {
      ...formData,
      voiceSpeed: Number(formData.voiceSpeed),
      voiceStability: Number(formData.voiceStability),
      voiceSimilarityBoost: Number(formData.voiceSimilarityBoost),
      voiceStyle: Number(formData.voiceStyle),
      maxDurationSeconds: Number(formData.maxDurationSeconds),
      ...(isEditing && {
        idleTimeoutSeconds: Number(formData.idleTimeoutSeconds),
        silenceTimeoutSeconds: Number(formData.silenceTimeoutSeconds),
        triggerMaxCount: Number(formData.triggerMaxCount),
        triggerResetMode: formData.triggerResetMode,
        idleMessages: formData.idleMessages,
        keypadInputPlanEnabled: formData.keypadInputPlanEnabled || false,
        keypadInputPlanTimeoutSeconds: Number(formData.keypadInputPlanTimeoutSeconds) || 5,
        keypadInputPlanDelimiters: Array.isArray(formData.keypadInputPlanDelimiters) && formData.keypadInputPlanDelimiters.length > 0
          ? formData.keypadInputPlanDelimiters
          : ['#']
      })
    };
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    const dataToSend = buildDataToSend();
    if (!dataToSend) return;
    onSave(dataToSend, tokenClient);
  };
  const inputStyle = "w-full px-3 py-2 mt-1 text-gray-200 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500";
  const labelStyle = "text-sm font-medium text-gray-300";
  const sectionHeaderStyle = "text-lg font-semibold mb-4 text-white";
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h2 className="text-2xl font-bold mb-6 text-white">
        {assistant?._id ? 'Editar Assistente' : 'Criar Novo Assistente'}
      </h2>
      {!assistant?._id && (
        <div className="p-3 mb-4 bg-blue-900 bg-opacity-50 rounded-lg border border-blue-700">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-blue-400 mr-2" />
            <p className="text-sm text-blue-300">
              As ferramentas, arquivos e configurações de mensagens ociosas podem ser associadas apenas durante a edição do assistente.
            </p>
          </div>
        </div>
      )}
      {/* Campos básicos */}
      <div>
        <label htmlFor="nome" className={labelStyle}>Nome do Assistente</label>
        <input
          id="nome"
          name="nome"
          type="text"
          value={formData.nome}
          onChange={handleInputChange}
          className={inputStyle}
          required
        />
      </div>

      {isEditing && (
        <div>
          <label htmlFor="firstMessageMode" className={labelStyle}>Modo de primeira mensagem</label>
          <select
            id="firstMessageMode"
            name="firstMessageMode"
            value={formData.firstMessageMode}
            onChange={handleInputChange}
            className={inputStyle}
          >
            <option value="assistant-speaks-first">O Assistente fala primeiro</option>
            <option value="assistant-waits-for-user">O Assistente aguarda o usuário</option>
          </select>
        </div>
      )}
      <div>

        <label htmlFor="firstMessage" className={labelStyle}>Mensagem Inicial</label>
        <input
          id="firstMessage"
          name="firstMessage"
          type="text"
          value={formData.firstMessage}
          onChange={handleInputChange}
          className={inputStyle}
          required
        />
      </div>
      <div>
        <label htmlFor="maxDurationSeconds" className={labelStyle}>Duração Máxima da Chamada (segundos)</label>
        <input
          id="maxDurationSeconds"
          name="maxDurationSeconds"
          type="number"
          value={formData.maxDurationSeconds}
          onChange={handleInputChange}
          className={inputStyle}
          min="10"
          max="43200"
          placeholder="600 (10 minutos)"
        />
        <p className="text-xs text-gray-400 mt-1">
          Valor entre 10 e 43200 segundos (12 horas). Padrão: 600 (10 minutos)
        </p>
      </div>
      <div>
        <label htmlFor="endCallMessage" className={labelStyle}>Mensagem de Fim de Chamada</label>
        <input
          id="endCallMessage"
          name="endCallMessage"
          type="text"
          value={formData.endCallMessage}
          onChange={handleInputChange}
          className={inputStyle}
          placeholder="Mensagem a ser reproduzida no final da chamada"
        />
      </div>
      <div>
        <label htmlFor="prompt" className={labelStyle}>Prompt do Sistema (IA)</label>
        <textarea
          id="prompt"
          name="prompt"
          value={formData.prompt}
          onChange={handleInputChange}
          className={`${inputStyle} h-32`}
          required
        />
      </div>
      {/* Configurações de voz */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="voiceProvider" className={labelStyle}>Provedor de Voz</label>
          <select
            id="voiceProvider"
            name="voiceProvider"
            value={formData.voiceProvider}
            onChange={handleInputChange}
            className={inputStyle}
            disabled
          >
            <option value="elevenlabs">DNET PRIME</option>
          </select>
        </div>
        <div>
          <label htmlFor="voiceId" className={labelStyle}>Voz</label>
          <select
            id="voiceId"
            name="voiceId"
            value={formData.voiceId}
            onChange={handleInputChange}
            className={inputStyle}
          >
            <option value="33B4UnXyTNbgLmdEDh5P">Lavinia (Feminino)</option>
            <option value="mPDAoQyGzxBSkE0OAOKw">Luiza (Feminino)</option>
            <option value="21m00Tcm4TlvDq8ikWAM">Rachel (Feminina)</option>
            <option value="EXAVITQu4vr4xnSDxMaL">Bella (Feminina)</option>
            <option value="MF3mGyEYCl7XYWbV9V6O">Elli (Feminina)</option>
            <option value="AZnzlk1XvdvUeBnXmlld">Domi (Feminina)</option>
            <option value="4r3G9XKliGgVZLKMgjik">Antônio (Masculino)</option>
            <option value="ErXwobaYiN019PkySvjV">Antoni (Masculino)</option>
            <option value="TxGEqnHWrfWFTfGW9XjX">Josh (Masculino)</option>
            <option value="yoZ06aMxZJJ28mfd3POQ">Sam (Masculino)</option>
            <option value="VR6AewLTigWG4xSOukaG">Arnold (Masculino)</option>
            <option value="flq6f7yk4E4fJM5XTYuZ">Michael (Masculino)</option>
          </select>
        </div>
      </div>
      {/* Ferramentas */}
      {isEditing && assistant?._id && (
        <div>
          <label htmlFor="tools" className={labelStyle}>Ferramentas</label>
          <div className="mt-1">
            <button
              type="button"
              onClick={onOpenToolsModal}
              className="w-full text-left px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200 hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {selectedTools.length === 0 ? (
                <span>Selecionar ferramentas</span>
              ) : (
                <span>{selectedTools.length} ferramenta(s) selecionada(s)</span>
              )}
            </button>
            {selectedTools.length > 0 && (
              <div className="mt-2 p-3 bg-gray-700 rounded-md">
                <h4 className="text-sm font-medium text-gray-200 mb-2">Ferramentas atualmente associadas:</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedTools.map((toolId, index) => (
                    <span
                      key={buildStableKey('form-selected-tool', toolId, index)}
                      className="px-2 py-1 bg-gray-600 rounded text-xs"
                    >
                      {toolId}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Arquivos (via modal) */}
      {isEditing && assistant?._id && (
        <div className="mt-4">
          <label className={labelStyle}>Arquivos</label>
          <div className="mt-1">
            <button
              type="button"
              onClick={onOpenFilesModal}
              className="w-full text-left px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200 hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {selectedFiles.length === 0 ? (
                <span>Selecionar arquivos</span>
              ) : (
                <span>{selectedFiles.length} arquivo(s) selecionado(s)</span>
              )}
            </button>
            {selectedFiles.length > 0 && (
              <div className="mt-2 p-3 bg-gray-700 rounded-md">
                <h4 className="text-sm font-medium text-gray-200 mb-2">Arquivos associados:</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedFiles.map((fileId, index) => (
                    <span
                      key={buildStableKey('form-selected-file', fileId, index)}
                      className="px-2 py-1 bg-gray-600 rounded text-xs"
                    >
                      {fileId}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Configurações avançadas de voz */}
      <div className="border-t border-gray-700 pt-6 mt-6">
        <div 
          className="flex items-center justify-between cursor-pointer mb-4"
          onClick={() => setIsVoiceAdvancedExpanded(!isVoiceAdvancedExpanded)}
        >
          <h3 className={sectionHeaderStyle}>Configurações Avançadas de Voz</h3>
          {isVoiceAdvancedExpanded ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          )}
        </div>
        {isVoiceAdvancedExpanded && (
          <>
            <p className="text-sm text-gray-400 mb-6">
              Ajuste os parâmetros da voz para obter o efeito desejado.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Velocidade */}
          <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
            <label htmlFor="voiceSpeed" className="block text-sm font-medium text-gray-300 mb-2">
              Velocidade ({Number(formData.voiceSpeed || 0).toFixed(1)})
            </label>
            <input
              id="voiceSpeed"
              name="voiceSpeed"
              type="range"
              min="0.7"
              max="1.2"
              step="0.1"
              value={formData.voiceSpeed}
              onChange={handleSliderChange}
              className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>Lento</span>
              <span>Rápido</span>
            </div>
          </div>
          {/* Estabilidade */}
          <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
            <label htmlFor="voiceStability" className="block text-sm font-medium text-gray-300 mb-2">
              Estabilidade ({Number(formData.voiceStability || 0).toFixed(1)})
            </label>
            <input
              id="voiceStability"
              name="voiceStability"
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={formData.voiceStability}
              onChange={handleSliderChange}
              className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>Menos estável</span>
              <span>Mais estável</span>
            </div>
          </div>
          {/* Similaridade */}
          <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
            <label htmlFor="voiceSimilarityBoost" className="block text-sm font-medium text-gray-300 mb-2">
              Similaridade ({Number(formData.voiceSimilarityBoost || 0).toFixed(1)})
            </label>
            <input
              id="voiceSimilarityBoost"
              name="voiceSimilarityBoost"
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={formData.voiceSimilarityBoost}
              onChange={handleSliderChange}
              className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>Menos similar</span>
              <span>Mais similar</span>
            </div>
          </div>
          {/* Estilo */}
          <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
            <label htmlFor="voiceStyle" className="block text-sm font-medium text-gray-300 mb-2">
              Exagero de Estilo ({Number(formData.voiceStyle || 0).toFixed(1)})
            </label>
            <input
              id="voiceStyle"
              name="voiceStyle"
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={formData.voiceStyle}
              onChange={handleSliderChange}
              className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>nenhum(mais rápido)</span>
              <span>Exagerado</span>
            </div>
          </div>
          {/* Som de fundo */}
          <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 md:col-span-3">
            <label htmlFor="backgroundSound" className="block text-sm font-medium text-gray-300 mb-2">
              Som de Fundo da Chamada
            </label>
            <select
              id="backgroundSound"
              name="backgroundSound"
              value={formData.backgroundSound}
              onChange={handleInputChange}
              className="w-full px-3 py-2 text-gray-200 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="office">Som de escritório (recomendado)</option>
              <option value="off">Sem som de fundo</option>
            </select>
            <p className="text-xs text-gray-400 mt-2">
              Defina se o assistente deve usar som ambiente de escritório durante a chamada ou permanecer com o fundo em silêncio total.
            </p>
          </div>
        </div>
          </>
        )}
      </div>
      {/* Configurações de mensagens ociosas */}
      {isEditing && (
        <div className="border-t border-gray-700 pt-6 mt-6">
          <div 
            className="flex items-center justify-between cursor-pointer mb-4"
            onClick={() => setIsIdleMessagesExpanded(!isIdleMessagesExpanded)}
          >
            <h3 className="text-lg font-semibold text-white">Configurações de Mensagens Ociosas</h3>
            {isIdleMessagesExpanded ? (
              <ChevronUpIcon className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
            )}
          </div>
          {isIdleMessagesExpanded && (
            <>
              <p className="text-sm text-gray-400 mb-6">
                Configure mensagens para quando o usuário ficar em silêncio por muito tempo.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
              <label htmlFor="idleTimeoutSeconds" className="block text-sm font-medium text-gray-300 mb-2">
                Tempo de espera para mensagem ociosa (segundos)
              </label>
              <input
                id="idleTimeoutSeconds"
                name="idleTimeoutSeconds"
                type="number"
                value={formData.idleTimeoutSeconds}
                onChange={handleInputChange}
                className="w-full px-3 py-2 text-gray-200 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                min="1"
                max="1000"
              />
              <p className="text-xs text-gray-400 mt-2">
                Tempo de silêncio antes de enviar a primeira mensagem (1-1000 segundos)
              </p>
            </div>
            <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
              <label htmlFor="silenceTimeoutSeconds" className="block text-sm font-medium text-gray-300 mb-2">
                Timeout de Silêncio (segundos)
              </label>
              <input
                id="silenceTimeoutSeconds"
                name="silenceTimeoutSeconds"
                type="number"
                value={formData.silenceTimeoutSeconds}
                onChange={handleInputChange}
                className="w-full px-3 py-2 text-gray-200 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                min="10"
                max="3600"
                step="1"
              />
              <p className="text-xs text-gray-400 mt-2">
                Tempo máximo de silêncio permitido antes de encerrar a chamada (10-3600 segundos)
              </p>
            </div>
            <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
              <label htmlFor="triggerMaxCount" className="block text-sm font-medium text-gray-300 mb-2">
                Número máximo de mensagens ociosas
              </label>
              <input
                id="triggerMaxCount"
                name="triggerMaxCount"
                type="number"
                value={formData.triggerMaxCount}
                onChange={handleInputChange}
                className="w-full px-3 py-2 text-gray-200 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                min="1"
                max="10"
              />
              <p className="text-xs text-gray-400 mt-2">
                Número máximo de mensagens a serem enviadas durante uma chamada (1-10)
              </p>
            </div>
          </div>
          <div className="mt-6">
            <label htmlFor="triggerResetMode" className="block text-sm font-medium text-gray-300 mb-2">
              Resetar contador de mensagens
            </label>
            <select
              id="triggerResetMode"
              name="triggerResetMode"
              value={formData.triggerResetMode}
              onChange={handleInputChange}
              className="w-full max-w-md px-3 py-2 text-gray-200 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="onUserSpeech">Quando o usuário falar novamente</option>
              <option value="never">Nunca resetar (máximo de mensagens por chamada)</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">
              Quando o contador de mensagens ociosas deve ser resetado
            </p>
          </div>
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Mensagens Ociosas
            </label>
            <div className="space-y-3">
              {formData.idleMessages.map((message, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={message}
                    onChange={(e) => {
                      const newMessages = [...formData.idleMessages];
                      newMessages[index] = e.target.value;
                      setFormData(prev => ({ ...prev, idleMessages: newMessages }));
                    }}
                    className="flex-grow px-3 py-2 text-gray-200 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder={`Mensagem ${index + 1}`}
                  />
                  {formData.idleMessages.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newMessages = formData.idleMessages.filter((_, i) => i !== index);
                        setFormData(prev => ({ ...prev, idleMessages: newMessages }));
                      }}
                      className="p-2 text-red-400 hover:text-red-300 transition-colors"
                      title="Remover mensagem"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    idleMessages: [...prev.idleMessages, `Nova mensagem ${prev.idleMessages.length + 1}`]
                  }));
                }}
                className="flex items-center text-purple-400 hover:text-purple-300 transition-colors mt-2"
              >
                <PlusCircleIcon className="h-5 w-5 mr-1" />
                Adicionar mensagem
              </button>
            </div>
          </div>
            </>
          )}
        </div>
      )}
      {/* Configurações de Keypad Input */}
      {isEditing && (
        <div className="border-t border-gray-700 pt-6 mt-6">
          <div 
            className="flex items-center justify-between cursor-pointer mb-4"
            onClick={() => setIsKeypadInputExpanded(!isKeypadInputExpanded)}
          >
            <h3 className="text-lg font-semibold text-white">Configurações de Entrada por Teclado</h3>
            {isKeypadInputExpanded ? (
              <ChevronUpIcon className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
            )}
          </div>
          {isKeypadInputExpanded && (
            <>
              <p className="text-sm text-gray-400 mb-6">
                Configure a entrada de dados via teclado do telefone durante a chamada.
              </p>

          {/* Toggle Switch para habilitar */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-300 block mb-1">Ativar Entrada por Teclado</span>
                <p className="text-xs text-gray-400">
                  Aceitar entrada do usuário via teclado do telefone
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    keypadInputPlanEnabled: !prev.keypadInputPlanEnabled,
                    // Garantir que keypadInputPlanDelimiters seja um array quando ativar
                    keypadInputPlanDelimiters: !prev.keypadInputPlanEnabled && (!Array.isArray(prev.keypadInputPlanDelimiters) || prev.keypadInputPlanDelimiters.length === 0)
                      ? ['#'] // Valor padrão se não existir ou estiver vazio
                      : prev.keypadInputPlanDelimiters
                  }));
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-800 ${formData.keypadInputPlanEnabled ? 'bg-purple-600' : 'bg-gray-600'
                  }`}
                role="switch"
                aria-checked={formData.keypadInputPlanEnabled}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.keypadInputPlanEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                />
              </button>
            </div>
          </div>

          {/* Configurações que aparecem apenas se habilitado */}
          {formData.keypadInputPlanEnabled && (
            <div className="space-y-6 bg-gray-800 p-4 rounded-lg border border-gray-700">
              {/* Timeout */}
              <div>
                <label htmlFor="keypadInputPlanTimeoutSeconds" className="block text-sm font-medium text-gray-300 mb-2">
                  Timeout (segundos)
                </label>
                <input
                  id="keypadInputPlanTimeoutSeconds"
                  name="keypadInputPlanTimeoutSeconds"
                  type="number"
                  value={formData.keypadInputPlanTimeoutSeconds}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-gray-200 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  min="0"
                  max="10"
                />
                <p className="text-xs text-gray-400 mt-2">
                  O número de segundos para aguardar antes de processar a entrada do teclado do usuário (0-10 segundos).
                  Defina como 0 para aguardar apenas um delimitador.
                </p>
              </div>

              {/* Delimitadores */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Delimitador
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Array.isArray(formData.keypadInputPlanDelimiters) && formData.keypadInputPlanDelimiters.includes('#')}
                      onChange={(e) => {
                        const currentDelimiters = Array.isArray(formData.keypadInputPlanDelimiters) ? formData.keypadInputPlanDelimiters : [];
                        if (e.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            keypadInputPlanDelimiters: [...currentDelimiters, '#']
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            keypadInputPlanDelimiters: currentDelimiters.filter(d => d !== '#')
                          }));
                        }
                      }}
                      className="w-5 h-5 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500 focus:ring-2"
                    />
                    <span className="text-sm text-gray-300 font-mono text-lg">#</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Array.isArray(formData.keypadInputPlanDelimiters) && formData.keypadInputPlanDelimiters.includes('*')}
                      onChange={(e) => {
                        const currentDelimiters = Array.isArray(formData.keypadInputPlanDelimiters) ? formData.keypadInputPlanDelimiters : [];
                        if (e.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            keypadInputPlanDelimiters: [...currentDelimiters, '*']
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            keypadInputPlanDelimiters: currentDelimiters.filter(d => d !== '*')
                          }));
                        }
                      }}
                      className="w-5 h-5 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500 focus:ring-2"
                    />
                    <span className="text-sm text-gray-300 font-mono text-lg">*</span>
                  </label>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Um caractere que será usado para determinar quando processar a entrada do teclado do usuário.
                  Se ambos delimitador e timeout estiverem definidos, o primeiro a ser alcançado causará o processamento da entrada.
                </p>
                {Array.isArray(formData.keypadInputPlanDelimiters) && formData.keypadInputPlanDelimiters.length === 0 && (
                  <p className="text-xs text-red-400 mt-2">
                    ⚠️ Selecione pelo menos um delimitador.
                  </p>
                )}
              </div>
            </div>
          )}
            </>
          )}
        </div>
      )}
      {/* Seção de Estruturas de Saída */}
      {isEditing && (
        <div className="mb-6 border-t border-gray-600 pt-6">
          <div 
            className="flex items-center justify-between cursor-pointer mb-3"
            onClick={() => setIsStructuredOutputsExpanded(!isStructuredOutputsExpanded)}
          >
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-lg text-white">Estruturas de Saída</h3>
              <span className="text-xs bg-yellow-500 text-black px-2 py-1 rounded-full font-semibold">
                {structuredOutputs.length}
              </span>
            </div>
            {isStructuredOutputsExpanded ? (
              <ChevronUpIcon className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
            )}
          </div>
          {isStructuredOutputsExpanded && (
            <>
              <p className="text-sm text-gray-400 mb-4">
                Extraia dados estruturados das conversas de chamadas. Use isso para capturar métricas de sucesso, resumos de chamadas, análise de sentimento, detalhes do cliente e muito mais.
              </p>
          {structuredOutputs.length > 0 ? (
            <>
              <p className="text-sm font-medium text-white mb-3">
                {structuredOutputs.length} estrutura{structuredOutputs.length !== 1 ? 's' : ''} de saída configurada{structuredOutputs.length !== 1 ? 's' : ''}
              </p>
              <p className="text-xs text-gray-400 mb-4">
                Os dados serão extraídos automaticamente após cada chamada.
              </p>
              <div className="space-y-3 mb-4">
                {structuredOutputs.map((output, index) => (
                  <div
                    key={buildStableKey('form-structured-output', output._id || output.id || output.name, index)}
                    className="bg-gray-700 border border-gray-600 rounded-lg p-4 flex items-start gap-3"
                  >
                    <FlagIcon className="h-5 w-5 text-gray-400 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-white">{output.name}</h4>
                        <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                          {output.schema?.type || 'N/A'}
                        </span>
                      </div>
                      {output.description && (
                        <p className="text-sm text-gray-300">{output.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-400 mb-4">
              Nenhuma estrutura de saída configurada.
            </p>
          )}
          {structuredOutputs.length > 0 && (
            <Button
              type="button"
              onClick={() => {
                onCancel();
                navigate('/call-logs');
              }}
              className="w-full bg-green-600 hover:bg-green-700 text-white mb-2"
            >
              <ChevronRightIcon className="h-4 w-4 mr-2" />
              Ver Todos os Logs de Chamadas
            </Button>
          )}
          <Button
            type="button"
            onClick={() => {
              if (!assistant?._id || !onSaveAndNavigate) {
                onCancel();
                navigate('/structured-outputs');
                return;
              }
              const dataToSend = buildDataToSend();
              if (!dataToSend) return;
              onSaveAndNavigate(dataToSend, tokenClient, '/structured-outputs');
            }}
            variant="outline"
            className="w-full"
          >
            <PlusCircleIcon className="h-4 w-4 mr-2" />
            Adicionar mais Estruturas de Saída
          </Button>
            </>
          )}
        </div>
      )}
      {/* Botões */}
      <div className="pt-4 flex justify-end gap-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-4 py-2 bg-gray-600 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 bg-purple-600 rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center"
        >
          {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
        </button>
      </div>
    </form>
  );
};
// Componente Principal
export default function Assistentes() {
  const ITENS_POR_PAGINA = 10;
  const [assistentes, setAssistentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingAssistant, setEditingAssistant] = useState(null);
  const [tools, setTools] = useState([]);
  const [selectedTools, setSelectedTools] = useState([]);
  const [isToolsModalOpen, setIsToolsModalOpen] = useState(false);
  const [toolsFiltro, setToolsFiltro] = useState('');
  const [selectedAssistantDetails, setSelectedAssistantDetails] = useState(null);
  const [structuredOutputs, setStructuredOutputs] = useState([]);
  const navigate = useNavigate();
  // Arquivos
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isFilesModalOpen, setIsFilesModalOpen] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [clienteFiltro, setClienteFiltro] = useState('');
  const [nomeFiltro, setNomeFiltro] = useState('');
  const [idFiltro, setIdFiltro] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const clienteId = userData.clienteId || userData._id || userData.id;
  const isSuperAdmin = userData.role === 'super_admin';
  const isTenantAdmin = userData.role === 'admin';
  const isAdmin = isTenantAdmin || isSuperAdmin;
  const tokenClient = localStorage.getItem('tokenClient');
  const buildStableKey = (prefix, idValue, index) => {
    if (idValue !== undefined && idValue !== null && String(idValue).trim() !== '') {
      return `${prefix}-${String(idValue)}`;
    }
    return `${prefix}-idx-${index}`;
  };
  const normalizeId = (value) => {
    if (!value) return '';
    if (typeof value === 'object') {
      if (value._id) return String(value._id);
      if (value.id) return String(value.id);
    }
    return String(value);
  };
  const clientesMap = useMemo(() => (
    new Map(clientes.map(c => [String(c._id), c]))
  ), [clientes]);
  const getAuthConfig = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };
  const fetchClientes = useCallback(async () => {
    if (!isAdmin) return;
    const config = getAuthConfig();
    if (!config) return;
    try {
      const res = await axios.get(`${baseUrl}/api/usuarios`, config);
      setClientes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erro ao buscar clientes:', err.response?.data || err.message);
    }
  }, [isAdmin]);
  const fetchAssistants = useCallback(async (tokenClient = null) => {
    try {
      setLoading(true);
      const params = !isAdmin && clienteId ? { clienteId } : {};
      if (tokenClient) {
        params.tokenClient = tokenClient;
      }
      const response = await axios.get(`${baseUrl}/api/assistentes/master`, {
        ...getAuthConfig(),
        params
      });
      setAssistentes(response.data);
      setError('');
    } catch (err) {
      setError('Falha ao carregar assistentes.');
      console.error('❌ Erro ao carregar assistentes:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, clienteId]);
  const fetchTools = useCallback(async (tokenClient = null) => {
    try {
      const params = !isAdmin && clienteId ? { clienteId } : {};
      if (tokenClient) {
        params.tokenClient = tokenClient;
      }
      const config = {
        ...getAuthConfig(),
        params
      };
      const response = await axios.get(`${baseUrl}/api/tools`, config);
      setTools(response.data);
    } catch (err) {
      console.error('Erro ao buscar ferramentas:', err.response?.data || err.message);
    }
  }, [isAdmin, clienteId]);


  const fetchFiles = useCallback(async (tokenClient = null) => {
    try {
      const params = {};
      if (tokenClient) {
        params.tokenClient = tokenClient;
      }
      if (clienteId) {
        params.clienteId = clienteId;
      }
      const config = {
        ...getAuthConfig(),
        params
      };
      const response = await axios.get(`${baseUrl}/api/files`, config);
      setFiles(response.data.files || []);
    } catch (err) {
      console.error('Erro ao buscar arquivos:', err.response?.data || err.message);
    }
  }, [clienteId]);

  const fetchStructuredOutputsForAssistant = async (assistantId) => {
    if (!assistantId) {
      setStructuredOutputs([]);
      return;
    }
    try {
      const config = getAuthConfig();
      if (!config) return;
      
      // Buscar todas as estruturas de saída e filtrar as que têm este assistantId
      const params = {};
      if (!isAdmin && clienteId) {
        params.clienteId = clienteId;
      }
      // const structuredOutputsBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
      const structuredOutputsBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net';
      const response = await axios.get(`${structuredOutputsBaseUrl}/api/structured-outputs`, {
        ...config,
        params
      });
      
      // Filtrar apenas as que têm este assistantId vinculado
      const outputs = Array.isArray(response.data) ? response.data : [];
      const assistantIdStr = String(assistantId);
      const linkedOutputs = outputs.filter(output => {
        const assistantIds = output.assistantIds || [];
        return assistantIds.some(id => String(id) === assistantIdStr);
      });
      
      setStructuredOutputs(linkedOutputs);
    } catch (err) {
      console.error('Erro ao buscar estruturas de saída:', err.response?.data || err.message);
      setStructuredOutputs([]);
    }
  };

  useEffect(() => {
    const tokenClientStored = localStorage.getItem('tokenClient');
    fetchAssistants(tokenClientStored);
  }, [fetchAssistants]);
  useEffect(() => {
    fetchClientes();
  }, [fetchClientes]);
  useEffect(() => {
    if (isModalOpen) {
      const tokenClientStored = localStorage.getItem('tokenClient');
      fetchTools(tokenClientStored);
      fetchFiles(tokenClientStored);
    }
  }, [isModalOpen, fetchTools, fetchFiles]);
  useEffect(() => {
    if (isModalOpen && editingAssistant) {
      let assistantTools = [];
      if (editingAssistant.tools) {
        assistantTools = Array.isArray(editingAssistant.tools)
          ? editingAssistant.tools.map(t => typeof t === 'object' ? t.vapiId || t.id : t)
          : [];
      }
      if (editingAssistant.toolsIds) {
        assistantTools = Array.isArray(editingAssistant.toolsIds)
          ? editingAssistant.toolsIds.map(t => typeof t === 'object' ? t.vapiId || t.id : t)
          : editingAssistant.toolsIds;
      }
      if (editingAssistant.config?.model?.toolIds) {
        assistantTools = Array.isArray(editingAssistant.config.model.toolIds)
          ? editingAssistant.config.model.toolIds.map(t => typeof t === 'object' ? t.id || t.vapiId : t)
          : editingAssistant.config.model.toolIds;
      }
      if (editingAssistant.config?.model?.toolsIds) {
        assistantTools = Array.isArray(editingAssistant.config.model.toolsIds)
          ? editingAssistant.config.model.toolsIds.map(t => typeof t === 'object' ? t.id || t.vapiId : t)
          : editingAssistant.config.model.toolsIds;
      }
      assistantTools = Array.isArray(assistantTools) ? assistantTools.filter(id => id) : [];
      setSelectedTools(assistantTools);
    }
  }, [isModalOpen, editingAssistant]);
  const getAssistantToolIds = (assistant) => {
    if (!assistant) return [];
    let assistantTools = [];
    if (assistant.tools) {
      assistantTools = Array.isArray(assistant.tools)
        ? assistant.tools.map(t => typeof t === 'object' ? (t.vapiId || t.id || t._id) : t)
        : [];
    }
    if (assistant.toolsIds) {
      assistantTools = Array.isArray(assistant.toolsIds)
        ? assistant.toolsIds.map(t => typeof t === 'object' ? (t.vapiId || t.id || t._id) : t)
        : assistant.toolsIds;
    }
    if (assistant.config?.model?.toolIds) {
      assistantTools = Array.isArray(assistant.config.model.toolIds)
        ? assistant.config.model.toolIds.map(t => typeof t === 'object' ? (t.id || t.vapiId || t._id) : t)
        : assistant.config.model.toolIds;
    }
    if (assistant.config?.model?.toolsIds) {
      assistantTools = Array.isArray(assistant.config.model.toolsIds)
        ? assistant.config.model.toolsIds.map(t => typeof t === 'object' ? (t.id || t.vapiId || t._id) : t)
        : assistant.config.model.toolsIds;
    }
    return Array.isArray(assistantTools) ? assistantTools.filter(Boolean) : [];
  };
  const handleOpenCreateModal = () => {
    setEditingAssistant(null);
    setSelectedTools([]);
    setSelectedFiles([]);
    setIsModalOpen(true);
  };
  const handleOpenEditModal = (assistant) => {
    const voiceConfig = assistant.config?.voice || {};
    const voiceModel = voiceConfig.model || 'eleven_multilingual_v2';
    const voiceSpeed = voiceConfig.speed !== undefined ? voiceConfig.speed : 1;
    const voiceStability = voiceConfig.stability !== undefined ? voiceConfig.stability : 0.5;
    const voiceSimilarityBoost = voiceConfig.similarityBoost !== undefined ? voiceConfig.similarityBoost : 0.8;
    const voiceStyle = voiceConfig.style !== undefined ? voiceConfig.style : 0.2;
    let idleTimeoutSeconds = assistant.idleTimeoutSeconds || 10;
    let triggerMaxCount = assistant.triggerMaxCount || 3;
    let triggerResetMode = assistant.triggerResetMode || 'onUserSpeech';
    let idleMessages = assistant.idleMessages || [
      "Você ainda está aí?",
      "Posso ajudar com mais alguma coisa?",
      "Estou aqui quando você estiver pronto para continuar."
    ];
    if (assistant.config?.hooks) {
      const idleHook = assistant.config.hooks.find(hook => hook.name === "idle_message_check");
      if (idleHook) {
        idleTimeoutSeconds = idleHook.options?.timeoutSeconds || idleTimeoutSeconds;
        triggerMaxCount = idleHook.options?.triggerMaxCount || triggerMaxCount;
        triggerResetMode = idleHook.options?.triggerResetMode || triggerResetMode;
        idleMessages = idleHook.do?.[0]?.exact || idleMessages;
      }
    }
    const normalizedData = {
      _id: assistant._id || assistant.id,
      vapiId: assistant.vapiId,
      nome: assistant.nome || assistant.name,
      prompt: assistant.model?.messages?.[0]?.content || assistant.prompt || '',
      firstMessage: assistant.firstMessage || '',
      voiceProvider: 'elevenlabs',
      voiceId: assistant.voiceId ||
        assistant.config?.voice?.voiceId ||
        '33B4UnXyTNbgLmdEDh5P',
      voiceModel,
      voiceSpeed,
      voiceStability,
      voiceSimilarityBoost,
      voiceStyle,
      backgroundSound: assistant.config?.backgroundSound || assistant.backgroundSound || 'office',
      clienteId: assistant.clienteId?._id || assistant.clienteId,
      tools: assistant.tools || assistant.toolsIds,
      maxDurationSeconds: assistant.maxDurationSeconds !== undefined ? assistant.maxDurationSeconds :
        assistant.config?.maxDurationSeconds !== undefined ? assistant.config.maxDurationSeconds :
          assistant.config?.model?.maxDurationSeconds !== undefined ? assistant.config.model.maxDurationSeconds :
            600,
      endCallMessage: assistant.endCallMessage !== undefined ? assistant.endCallMessage :
        assistant.config?.endCallMessage !== undefined ? assistant.config.endCallMessage :
          assistant.config?.model?.endCallMessage !== undefined ? assistant.config.model.endCallMessage :
            '',
      idleTimeoutSeconds,
      triggerMaxCount,
      triggerResetMode,
      idleMessages,
      silenceTimeoutSeconds: assistant.silenceTimeoutSeconds !== undefined
        ? assistant.silenceTimeoutSeconds
        : assistant.config?.silenceTimeoutSeconds !== undefined
          ? assistant.config.silenceTimeoutSeconds
          : 10,
      firstMessageMode: assistant.firstMessageMode !== undefined ? assistant.firstMessageMode : 'assistant-speaks-first',
      keypadInputPlanEnabled: assistant.config?.keypadInputPlan?.enabled !== undefined
        ? assistant.config.keypadInputPlan.enabled
        : false,
      keypadInputPlanTimeoutSeconds: assistant.config?.keypadInputPlan?.timeoutSeconds !== undefined
        ? assistant.config.keypadInputPlan.timeoutSeconds
        : 5,
      keypadInputPlanDelimiters: Array.isArray(assistant.config?.keypadInputPlan?.delimiters) && assistant.config.keypadInputPlan.delimiters.length > 0
        ? assistant.config.keypadInputPlan.delimiters
        : ['#'],
      config: assistant.config,
      files: assistant.files || assistant.config?.files || [],
      knowledgeBase: assistant.knowledgeBase || {} // Adicionando o knowledgeBase
    };
    // Inicializa arquivos selecionados a partir do assistente, se já houver
    let assistantFileIds = [];
    if (Array.isArray(normalizedData.files)) {
      assistantFileIds = normalizedData.files.map(f =>
        typeof f === 'object' ? (f.id || f._id || f.key) : f
      ).filter(Boolean);
    }
    // Verifica se o knowledgeBase está presente e tem fileIds
    if (normalizedData.knowledgeBase && Array.isArray(normalizedData.knowledgeBase.fileIds)) {
      assistantFileIds = [...assistantFileIds, ...normalizedData.knowledgeBase.fileIds];
    }
    setEditingAssistant(normalizedData);
    setSelectedFiles(assistantFileIds);
    setIsModalOpen(true);
  };

  const handleSaveAssistant = async (formData, tokenClient = null, options = {}) => {
    const { redirectTo = null, showSuccessAlert = true } = options;
    setIsSubmitting(true);
    const isEditing = !!editingAssistant?._id;
    const url = isEditing
      ? `${baseUrl}/api/assistentes/master/${editingAssistant.vapiId}`
      : `${baseUrl}/api/assistentes/master`;
    const method = isEditing ? 'put' : 'post';

    // Adicionando o knowledgeBase ao payload
    const payload = {
      nome: formData.nome,
      prompt: formData.prompt,
      firstMessage: formData.firstMessage,
      firstMessageMode: formData.firstMessageMode, // Adicione esta linha
      voiceId: formData.voiceId,
      bodyType: formData.voiceProvider,
      clienteId: formData.clienteId || clienteId,
      maxDurationSeconds: Number(formData.maxDurationSeconds),
      endCallMessage: formData.endCallMessage,
      voiceSpeed: Number(formData.voiceSpeed),
      voiceStability: Number(formData.voiceStability),
      voiceSimilarityBoost: Number(formData.voiceSimilarityBoost),
      voiceStyle: Number(formData.voiceStyle),
      voiceModel: formData.voiceModel,
      backgroundSound: formData.backgroundSound || 'office',
      knowledgeBase: {
        fileIds: selectedFiles, // Adicionando os IDs dos arquivos selecionados
        provider: "google" // Adicionando o provedor
      }
    };

    if (tokenClient) {
      payload.tokenClient = tokenClient;
    }

    if (isEditing) {
      payload.silenceTimeoutSeconds = Number(formData.silenceTimeoutSeconds);
      payload.idleTimeoutSeconds = Number(formData.idleTimeoutSeconds);
      payload.triggerMaxCount = Number(formData.triggerMaxCount);
      payload.triggerResetMode = formData.triggerResetMode;
      payload.idleMessages = formData.idleMessages;
      payload.hooks = [
        {
          type: "idle_message",
          options: {
            timeoutSeconds: Number(formData.idleTimeoutSeconds),
            triggerMaxCount: Number(formData.triggerMaxCount),
            triggerResetMode: formData.triggerResetMode
          },
          messages: formData.idleMessages
        }
      ];

      // Adicionar configurações do Keypad Input Plan
      const delimiters = Array.isArray(formData.keypadInputPlanDelimiters) && formData.keypadInputPlanDelimiters.length > 0
        ? formData.keypadInputPlanDelimiters
        : ['#'];
      const timeout = Number(formData.keypadInputPlanTimeoutSeconds) || 5;
      payload.keypadInputPlan = {
        enabled: formData.keypadInputPlanEnabled || false,
        delimiters: delimiters,
        timeoutSeconds: Math.max(0, Math.min(10, timeout)) // Garantir entre 0 e 10
      };

      if (selectedTools.length > 0) {
        payload.tools = selectedTools;
      }
    }

    if (isEditing && editingAssistant.config) {
      const { hooks, ...otherConfig } = editingAssistant.config;
      payload.config = otherConfig;
    }

    try {
      await axios[method](url, payload, getAuthConfig());
      setIsModalOpen(false);
      setSelectedTools([]);
      setSelectedFiles([]);
      const tokenClientStored = localStorage.getItem('tokenClient');
      fetchAssistants(tokenClientStored);
      if (showSuccessAlert) {
        alert(isEditing ? 'Assistente atualizado com sucesso!' : 'Assistente criado com sucesso!');
      }
      if (redirectTo) {
        navigate(redirectTo);
      }
      return true;
    } catch (err) {
      console.error('❌ Erro ao salvar assistente:', err.response?.data || err.message);
      let userMessage = 'Ocorreu um erro ao salvar o assistente.';
      let details = '';
      if (err.response?.data) {
        if (err.response.data.message) {
          if (Array.isArray(err.response.data.message)) {
            userMessage = err.response.data.message.join(', ');
          } else {
            userMessage = err.response.data.message;
          }
        }
        if (err.response.data.details) {
          details = Array.isArray(err.response.data.details)
            ? err.response.data.details.join('\n')
            : err.response.data.details;
        }
      }
      if (err.response?.data?.message?.includes('hooks.property')) {
        const fallbackPayload = { ...payload };
        delete fallbackPayload.hooks;
        fallbackPayload.idleTimeoutSeconds = Number(formData.idleTimeoutSeconds);
        fallbackPayload.triggerMaxCount = Number(formData.triggerMaxCount);
        fallbackPayload.triggerResetMode = formData.triggerResetMode;
        fallbackPayload.idleMessages = formData.idleMessages;
        try {
          await axios[method](url, fallbackPayload, getAuthConfig());
          setIsModalOpen(false);
          setSelectedTools([]);
          setSelectedFiles([]);
          const tokenClientStored = localStorage.getItem('tokenClient');
          fetchAssistants(tokenClientStored);
          if (showSuccessAlert) {
            alert(isEditing ? 'Assistente atualizado com sucesso!' : 'Assistente criado com sucesso!');
          }
          if (redirectTo) {
            navigate(redirectTo);
          }
          return true;
        } catch (fallbackErr) {
          console.error('❌ Falha na tentativa alternativa:', fallbackErr.response?.data || fallbackErr.message);
          userMessage = 'Primeira tentativa falhou devido a hooks inválidos e a tentativa alternativa também falhou.';
          details = fallbackErr.response?.data?.message || 'Verifique os dados e tente novamente.';
        }
      }
      alert(`${userMessage}\n${details || 'Verifique os dados e tente novamente.'}`);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleSaveAssistantAndNavigate = async (formData, tokenClientValue = null, redirectTo = '/structured-outputs') => {
    await handleSaveAssistant(formData, tokenClientValue, { redirectTo, showSuccessAlert: false });
  };

  const handleDeleteAssistant = async (assistant, tokenClient = null) => {
    if (window.confirm(`Tem certeza que deseja excluir o assistente "${assistant.nome || assistant.name}"?`)) {
      try {
        const config = getAuthConfig();
        const url = `${baseUrl}/api/assistentes/vapi/${assistant.vapiId}`;
        const params = tokenClient ? { tokenClient } : {};
        await axios.delete(url, { ...config, params });
        const storedTokenClient = localStorage.getItem('tokenClient');
        fetchAssistants(storedTokenClient);
      } catch (err) {
        console.error('Erro ao excluir assistente:', err.response?.data || err.message);
        alert('Erro ao excluir. Tente novamente.');
      }
    }
  };

  const handleDuplicateAssistant = async (assistant) => {
    const apiKey = window.prompt(
      'Informe a API Key para criar a cópia no sistema. Deixe a atual ou cole outra:',
      tokenClient || ''
    );
    if (apiKey === null) return;

    const { default: toastApi } = await import('react-hot-toast');

    if (!apiKey.trim()) {
      toastApi.error('API Key é obrigatória para duplicar.');
      return;
    }

    const toastId = toastApi.loading('Duplicando assistente...');
    try {
      const config = getAuthConfig();
      await axios.post(
        `${baseUrl}/api/assistentes/master/${assistant._id || assistant.vapiId}/duplicate`,
        {
          tokenClient: apiKey.trim(),
          clienteId:
            normalizeId(clienteFiltro || clienteId) || normalizeId(assistant.clienteId),
          nome: `${assistant.nome || assistant.name || 'Assistente'} (cópia)`
        },
        config
      );
      toastApi.success('Assistente duplicado com sucesso!', { id: toastId });
      fetchAssistants(tokenClient);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao duplicar assistente.';
      toastApi.error(errorMessage, { id: toastId });
      console.error('Erro ao duplicar assistente:', err);
    }
  };
  const handleOpenToolsModal = () => {
    setIsToolsModalOpen(true);
  };
  const handleCloseToolsModal = () => {
    setIsToolsModalOpen(false);
    setToolsFiltro('');
  };
  const handleToolSelection = (toolId) => {
    setSelectedTools(prev => {
      const isSelected = prev.includes(toolId);
      return isSelected ? prev.filter(id => id !== toolId) : [...prev, toolId];
    });
  };
  const handleSaveToolSelection = () => {
    setIsToolsModalOpen(false);
  };
  const handleOpenFilesModal = () => {
    setIsFilesModalOpen(true);
  };
  const handleCloseFilesModal = () => {
    setIsFilesModalOpen(false);
  };
  const handleUploadFile = async (file) => {
    try {
      setIsUploadingFile(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tokenClient', tokenClient);
      formData.append('clienteId', clienteId); // Adicione o clienteId aqui
      const authConfig = getAuthConfig();
      const config = {
        ...authConfig,
        headers: {
          ...authConfig.headers,
          'Content-Type': 'multipart/form-data',
        },
      };
      const response = await axios.post(
        `${baseUrl}/api/files/upload`,
        formData,
        config
      );
      const uploadedFile = response.data.file || response.data;
      const fileId = uploadedFile.id || uploadedFile._id || uploadedFile.key;
      setFiles(prev => [...prev, uploadedFile]);
      setSelectedFiles(prev => prev.includes(fileId) ? prev : [...prev, fileId]);
    } catch (err) {
      console.error('Erro ao enviar arquivo:', err.response?.data || err.message);
      alert('Erro ao enviar arquivo. Tente novamente.');
    } finally {
      setIsUploadingFile(false);
    }
  };

  const assistentesFiltrados = useMemo(() => {
    let filtrados = assistentes;

    // Admin (não super) não deve ver assistentes sem cliente (N/A)
    if (isTenantAdmin) {
      filtrados = filtrados.filter(assistant => !!normalizeId(assistant.clienteId));
    }
    
    // Filtro por cliente (apenas admin)
    if (isAdmin && clienteFiltro) {
      filtrados = filtrados.filter(assistant => normalizeId(assistant.clienteId) === clienteFiltro);
    }
    
    // Filtro por nome
    if (nomeFiltro) {
      const nomeLower = nomeFiltro.toLowerCase();
      filtrados = filtrados.filter(assistant => {
        const nome = (assistant.nome || assistant.name || '').toLowerCase();
        return nome.includes(nomeLower);
      });
    }
    
    // Filtro por ID (ID do provedor)
    if (idFiltro) {
      const idLower = idFiltro.toLowerCase();
      filtrados = filtrados.filter(assistant => {
        const vapiId = (assistant.vapiId || assistant.id || '').toLowerCase();
        return vapiId.includes(idLower);
      });
    }
    
    return filtrados;
  }, [assistentes, isAdmin, isTenantAdmin, clienteFiltro, nomeFiltro, idFiltro]);

  const toolsFiltradas = useMemo(() => {
    const termo = (toolsFiltro || '').trim().toLowerCase();
    if (!termo) return tools;
    return tools.filter((tool) => {
      const nome = String(tool.name || tool.nome || tool.function?.name || '').toLowerCase();
      const descricao = String(tool.description || tool.descricao || tool.function?.description || '').toLowerCase();
      const tipo = String(tool.type || '').toLowerCase();
      const id = String(tool.vapiId || tool.id || tool._id || '').toLowerCase();
      return nome.includes(termo) || descricao.includes(termo) || tipo.includes(termo) || id.includes(termo);
    });
  }, [tools, toolsFiltro]);
  const totalPaginas = useMemo(() => Math.max(1, Math.ceil(assistentesFiltrados.length / ITENS_POR_PAGINA)), [assistentesFiltrados.length]);
  const assistentesPaginados = useMemo(() => {
    const inicio = (paginaAtual - 1) * ITENS_POR_PAGINA;
    return assistentesFiltrados.slice(inicio, inicio + ITENS_POR_PAGINA);
  }, [assistentesFiltrados, paginaAtual]);
  useEffect(() => {
    setPaginaAtual(1);
  }, [nomeFiltro, idFiltro, clienteFiltro]);
  useEffect(() => {
    if (paginaAtual > totalPaginas) {
      setPaginaAtual(totalPaginas);
    }
  }, [paginaAtual, totalPaginas]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Gerenciador de Assistentes</h1>
          <p className="text-sm text-muted-foreground">Configure comportamento, voz e integrações.</p>
        </div>
        <Button onClick={handleOpenCreateModal}>
          <PlusCircleIcon className="h-5 w-5" />
          Criar Assistente
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Nome do Assistente</label>
          <input
            type="text"
            value={nomeFiltro}
            onChange={(e) => setNomeFiltro(e.target.value)}
            placeholder="Filtrar por nome..."
            className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">ID</label>
          <input
            type="text"
            value={idFiltro}
            onChange={(e) => setIdFiltro(e.target.value)}
            placeholder="Filtrar por ID..."
            className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </div>
        {isAdmin && (
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Cliente</label>
            <select
              value={clienteFiltro}
              onChange={(e) => setClienteFiltro(e.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="">Todos os clientes</option>
              {clientes.map((cliente, index) => (
                <option
                  key={buildStableKey('cliente', cliente._id || cliente.id || cliente.email, index)}
                  value={String(cliente._id)}
                >
                  {cliente.nome || cliente.name || cliente.email || String(cliente._id)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      {(nomeFiltro || idFiltro || (isAdmin && clienteFiltro)) && (
        <div className="flex justify-end">
          <button
            onClick={() => {
              setNomeFiltro('');
              setIdFiltro('');
              if (isAdmin) setClienteFiltro('');
            }}
            className="text-sm text-muted-foreground hover:text-foreground underline"
          >
            Limpar filtros
          </button>
        </div>
      )}
      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-muted/50 text-muted-foreground">
            <tr>
              <th className="p-4 font-semibold">Nome</th>
              <th className="p-4 font-semibold">Mensagem Inicial</th>
              {isAdmin && <th className="p-4 font-semibold">Cliente</th>}
              <th className="p-4 font-semibold">Provedor</th>
              <th className="p-4 font-semibold text-center">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={isAdmin ? 5 : 4} className="text-center p-8 text-muted-foreground">
                  <div className="flex justify-center items-center gap-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    Carregando assistentes...
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={isAdmin ? 5 : 4} className="text-center p-8 text-destructive">
                  <div className="flex items-center justify-center">
                    <ExclamationTriangleIcon className="h-6 w-6 mr-2" />
                    {error}
                  </div>
                </td>
              </tr>
            ) : assistentesFiltrados.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 5 : 4} className="text-center p-8 text-muted-foreground">
                  Nenhum assistente encontrado. Crie um clicando no botão acima.
                </td>
              </tr>
            ) : (
              assistentesPaginados.map((assistant, index) => (
                <tr
                  key={buildStableKey('assistant', assistant.vapiId || assistant._id || assistant.id, index)}
                  className="border-b border-border transition-colors hover:bg-muted/40"
                >
                  <td className="p-4 font-medium">{assistant.nome || assistant.name}</td>
                  <td className="p-4 truncate max-w-xs" title={assistant.firstMessage}>
                    {assistant.firstMessage?.substring(0, 30)}{assistant.firstMessage?.length > 30 ? '...' : ''}
                  </td>
                  {isAdmin && (
                    <td className="p-4">
                      {(() => {
                        const clienteIdValue = normalizeId(assistant.clienteId);
                        const cliente = clientesMap.get(clienteIdValue);
                        return cliente?.nome || cliente?.name || cliente?.email || clienteIdValue || 'N/A';
                      })()}
                    </td>
                  )}
                  <td className="p-4">
                    <span className="font-semibold">
                      {assistant.voice?.provider || assistant.config?.voice?.provider === '11labs' ? 'DNET PRIME' : 'DNET PRO'}
                    </span>
                  </td>
                  <td className="p-4 text-center flex justify-center items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedAssistantDetails(assistant);
                        fetchStructuredOutputsForAssistant(assistant.vapiId || assistant.id);
                      }}
                      title="Ver detalhes do Assistente"
                    >
                      <CpuChipIcon className="h-5 w-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenEditModal(assistant)}
                      title="Editar Assistente"
                    >
                      <PencilSquareIcon className="h-5 w-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDuplicateAssistant(assistant)}
                      title="Duplicar Assistente"
                    >
                      <DocumentDuplicateIcon className="h-5 w-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteAssistant(assistant, tokenClient)}
                      title="Excluir Assistente"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
      {assistentesFiltrados.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Mostrando {assistentesPaginados.length} de {assistentesFiltrados.length} assistentes
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setPaginaAtual((p) => Math.max(1, p - 1))}
              disabled={paginaAtual === 1}
            >
              Anterior
            </Button>
            <span className="text-sm text-muted-foreground">
              Página {paginaAtual} de {totalPaginas}
            </span>
            <Button
              variant="outline"
              onClick={() => setPaginaAtual((p) => Math.min(totalPaginas, p + 1))}
              disabled={paginaAtual === totalPaginas}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}
      {/* Modal para o formulário de assistente */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <AssistantForm
          assistant={editingAssistant}
          onSave={handleSaveAssistant}
          onSaveAndNavigate={handleSaveAssistantAndNavigate}
          onCancel={() => setIsModalOpen(false)}
          isSubmitting={isSubmitting}
          selectedTools={selectedTools}
          onOpenToolsModal={handleOpenToolsModal}
          tokenClient={tokenClient}
          selectedFiles={selectedFiles}
          onOpenFilesModal={handleOpenFilesModal}
          files={files}
          setSelectedFiles={setSelectedFiles}
          isUploading={isUploadingFile}
          onUpload={handleUploadFile}
        />
      </Modal>
      {/* Modal para seleção de ferramentas */}
      <Modal isOpen={isToolsModalOpen} onClose={handleCloseToolsModal}>
        <div className="space-y-4">
          <h2 className="text-2xl font-bold mb-6 text-white">Selecionar Ferramentas</h2>
          {selectedTools.length > 0 && (
            <div className="bg-gray-700 p-3 rounded-lg mb-4">
              <h3 className="font-medium text-white mb-2">Ferramentas atualmente associadas:</h3>
              <div className="flex flex-wrap gap-2">
                {selectedTools.map((toolId, index) => {
                  const tool = tools.find(t => (t.vapiId || t.id || t._id) === toolId);
                  return (
                    <div
                      key={buildStableKey('selected-tool', toolId, index)}
                      className="bg-gray-600 px-3 py-1 rounded-full text-sm flex items-center"
                    >
                      {tool?.name || tool?.nome || toolId}
                      <button
                        onClick={() => handleToolSelection(toolId)}
                        className="ml-2 text-red-400 hover:text-red-300"
                        title="Remover ferramenta"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {tools.length === 0 ? (
            <div className="text-gray-400 py-4 text-center">
              <ExclamationTriangleIcon className="h-10 w-10 mx-auto mb-2" />
              <p>Nenhuma ferramenta disponível.</p>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Filtrar ferramentas..."
                  value={toolsFiltro}
                  onChange={(e) => setToolsFiltro(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto pr-2">
                {toolsFiltradas.map((tool, index) => {
                  const toolId = tool.vapiId || tool.id || tool._id;
                  const isSelected = selectedTools.includes(toolId);
                  const toolName = tool.name || tool.nome || tool.function?.name || 'Ferramenta sem nome';
                  const toolDescription = tool.description || tool.descricao || 'Sem descrição';
                  return (
                    <div
                      key={buildStableKey('tool', toolId || toolName, index)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${isSelected ? 'border-purple-500 bg-gray-700' : 'border-gray-600 hover:bg-gray-700'}`}
                      onClick={() => handleToolSelection(toolId)}
                    >
                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => { }}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 text-purple-600 rounded mt-1"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex justify-between items-start">
                            <h3 className="font-medium text-white">{toolName}</h3>
                            {tool.category && (
                              <span className="text-xs bg-gray-600 px-2 py-1 rounded-full">
                                {tool.category}
                              </span>
                            )}
                          </div>
                          <p className="text-gray-400 text-sm mt-1">{toolDescription}</p>
                          <p className="text-gray-500 text-xs mt-1">ID: {toolId}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {toolsFiltradas.length === 0 && (
                <div className="text-gray-400 py-4 text-center">
                  Nenhuma ferramenta encontrada para o filtro informado.
                </div>
              )}
            </>
          )}
          <div className="pt-4 flex justify-end gap-4">
            <button
              type="button"
              onClick={handleCloseToolsModal}
              className="px-4 py-2 bg-gray-600 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSaveToolSelection}
              className="px-4 py-2 bg-purple-600 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              Salvar Seleção
            </button>
          </div>
        </div>
      </Modal>
      {/* Modal para seleção de arquivos */}
      <Modal isOpen={isFilesModalOpen} onClose={handleCloseFilesModal}>
        <div className="space-y-4">
          <h2 className="text-2xl font-bold mb-6 text-white">Selecionar Arquivos</h2>
          {/* Arquivos associados ao assistente */}
          {selectedFiles.length > 0 && (
            <div className="bg-gray-700 p-3 rounded-lg mb-4">
              <h3 className="font-medium text-white mb-2">Arquivos associados:</h3>
              <div className="flex flex-wrap gap-2">
                {selectedFiles.map((fileId, index) => {
                  const file = files.find(f => (f.id || f._id || f.key) === fileId);
                  return (
                    <div
                      key={buildStableKey('selected-file', fileId, index)}
                      className="bg-gray-600 px-3 py-1 rounded-full text-sm flex items-center"
                    >
                      {file?.originalName || file?.name || fileId}
                      <button
                        onClick={() => {
                          setSelectedFiles(prev => prev.filter(id => id !== fileId));
                        }}
                        className="ml-2 text-red-400 hover:text-red-300"
                        title="Remover arquivo"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Upload de novo arquivo */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Enviar novo arquivo
            </label>
            <FileUploadButton
              isUploading={isUploadingFile}
              onUpload={handleUploadFile}
            />
          </div>
          {/* Lista de arquivos disponíveis */}
          {files.length === 0 ? (
            <div className="text-gray-400 py-4 text-center">
              <ExclamationTriangleIcon className="h-10 w-10 mx-auto mb-2" />
              <p>Nenhum arquivo disponível.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 max-h-96 overflow-y-auto pr-2">
              {files.map((file, index) => {
                const fileId = file.id || file._id || file.key;
                const isSelected = selectedFiles.includes(fileId);
                const fileName = file.originalName || file.name || fileId;
                const fileStatus = file.status || 'done';
                return (
                  <div
                    key={buildStableKey('file', fileId || fileName, index)}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${isSelected ? 'border-purple-500 bg-gray-700' : 'border-gray-600 hover:bg-gray-700'
                      }`}
                    onClick={() => {
                      setSelectedFiles(prev => {
                        const already = prev.includes(fileId);
                        return already ? prev.filter(id => id !== fileId) : [...prev, fileId];
                      });
                    }}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium text-white">{fileName}</span>
                      <span className="text-xs text-gray-400 mt-1">ID: {fileId}</span>
                      <span className="text-xs text-gray-400 mt-1">
                        Status: {fileStatus}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="pt-4 flex justify-end gap-4">
            <button
              type="button"
              onClick={handleCloseFilesModal}
              className="px-4 py-2 bg-gray-600 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </Modal>
      {/* Modal de detalhes do assistente */}
      <Modal
        isOpen={!!selectedAssistantDetails}
        onClose={() => setSelectedAssistantDetails(null)}
      >
        {selectedAssistantDetails && (
          <div>
            <h2 className="text-2xl font-bold mb-6 text-white">
              Detalhes do Assistente
            </h2>
            <div className="mb-4">
              <h3 className="font-semibold text-lg mb-2">Informações Básicas</h3>
              <p>
                <span className="font-medium">ID:</span>{' '}
                {selectedAssistantDetails.vapiId || '—'}
              </p>
              <p>
                <span className="font-medium">Nome:</span>{' '}
                {selectedAssistantDetails.nome || selectedAssistantDetails.name || '—'}
              </p>
              <p>
                <span className="font-medium">Cliente:</span>{' '}
                {selectedAssistantDetails.clienteId?.nome ||
                  selectedAssistantDetails.clienteId ||
                  'N/A'}
              </p>
              <p>
                <span className="font-medium">Mensagem Inicial:</span>{' '}
                {selectedAssistantDetails.firstMessage || '—'}
              </p>
            </div>
            <div className="mb-4">
              <h3 className="font-semibold text-lg mb-2">Configuração de Voz</h3>
              <p>
                <span className="font-medium">Provedor:</span>{' '}
                {(selectedAssistantDetails.voice?.provider ||
                  selectedAssistantDetails.config?.voice?.provider) === '11labs'
                  ? 'DNET PRIME'
                  : 'DNET PRO'}
              </p>
              <p>
                <span className="font-medium">Voz (voiceId):</span>{' '}
                {selectedAssistantDetails.voiceId ||
                  selectedAssistantDetails.config?.voice?.voiceId ||
                  '—'}
              </p>
              <p>
                <span className="font-medium">Velocidade:</span>{' '}
                {selectedAssistantDetails.voiceSpeed ??
                  selectedAssistantDetails.config?.voice?.speed ??
                  1}
              </p>
              <p>
                <span className="font-medium">Estabilidade:</span>{' '}
                {selectedAssistantDetails.voiceStability ??
                  selectedAssistantDetails.config?.voice?.stability ??
                  0.5}
              </p>
              <p>
                <span className="font-medium">Similaridade:</span>{' '}
                {selectedAssistantDetails.voiceSimilarityBoost ??
                  selectedAssistantDetails.config?.voice?.similarityBoost ??
                  0.8}
              </p>
              <p>
                <span className="font-medium">Estilo:</span>{' '}
                {selectedAssistantDetails.voiceStyle ??
                  selectedAssistantDetails.config?.voice?.style ??
                  0.2}
              </p>
            </div>
            <div className="mb-4">
              <h3 className="font-semibold text-lg mb-2">Configuração de Chamada</h3>
              <p>
                <span className="font-medium">Duração Máxima (segundos):</span>{' '}
                {selectedAssistantDetails.maxDurationSeconds ??
                  selectedAssistantDetails.config?.maxDurationSeconds ??
                  selectedAssistantDetails.config?.model?.maxDurationSeconds ??
                  600}
              </p>
              <p>
                <span className="font-medium">Mensagem de Fim:</span>{' '}
                {selectedAssistantDetails.endCallMessage ??
                  selectedAssistantDetails.config?.endCallMessage ??
                  selectedAssistantDetails.config?.model?.endCallMessage ??
                  '—'}
              </p>
            </div>
            <div className="mb-4">
              <h3 className="font-semibold text-lg mb-2">Mensagens Ociosas</h3>
              <p>
                <span className="font-medium">Timeout (segundos):</span>{' '}
                {selectedAssistantDetails.idleTimeoutSeconds ??
                  selectedAssistantDetails.config?.idleTimeoutSeconds ??
                  10}
              </p>
              <p>
                <span className="font-medium">Máx. mensagens:</span>{' '}
                {selectedAssistantDetails.triggerMaxCount ??
                  selectedAssistantDetails.config?.triggerMaxCount ??
                  3}
              </p>
              <p>
                <span className="font-medium">Modo de reset:</span>{' '}
                {selectedAssistantDetails.triggerResetMode ??
                  selectedAssistantDetails.config?.triggerResetMode ??
                  'onUserSpeech'}
              </p>
              <div className="mt-2">
                <span className="font-medium">Mensagens:</span>
                <ul className="list-disc list-inside text-sm mt-1 text-gray-200">
                  {(selectedAssistantDetails.idleMessages || [])
                    .slice(0, 5)
                    .map((msg, idx) => (
                      <li key={idx}>{msg}</li>
                    ))}
                  {selectedAssistantDetails.idleMessages &&
                    selectedAssistantDetails.idleMessages.length > 5 && (
                      <li className="text-gray-400">
                        + {selectedAssistantDetails.idleMessages.length - 5} outras...
                      </li>
                    )}
                </ul>
              </div>
            </div>
            <div className="mb-4">
              <h3 className="font-semibold text-lg mb-2">Ferramentas Associadas</h3>
              {(() => {
                const toolIds = getAssistantToolIds(selectedAssistantDetails);
                if (!toolIds || toolIds.length === 0) {
                  return <p>Nenhuma ferramenta associada.</p>;
                }
                return (
                  <ul className="list-disc list-inside text-sm text-gray-200">
                    {toolIds.map((id) => (
                      <li key={id}>{id}</li>
                    ))}
                  </ul>
                );
              })()}
            </div>
            {/* Seção de Estruturas de Saída */}
            <div className="mb-4 border-t border-gray-600 pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-lg">Estruturas de Saída</h3>
                <span className="text-xs bg-yellow-500 text-black px-2 py-1 rounded-full font-semibold">
                  {structuredOutputs.length}
                </span>
              </div>
              <p className="text-sm text-gray-400 mb-4">
                Extraia dados estruturados das conversas de chamadas. Use isso para capturar métricas de sucesso, resumos de chamadas, análise de sentimento, detalhes do cliente e muito mais.
              </p>
              {structuredOutputs.length > 0 ? (
                <>
                  <p className="text-sm font-medium mb-3">
                    {structuredOutputs.length} estrutura{structuredOutputs.length !== 1 ? 's' : ''} de saída configurada{structuredOutputs.length !== 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-gray-400 mb-4">
                    Os dados serão extraídos automaticamente após cada chamada.
                  </p>
                  <div className="space-y-3 mb-4">
                    {structuredOutputs.map((output, index) => (
                      <div
                        key={buildStableKey('structured-output', output._id || output.id || output.name, index)}
                        className="bg-gray-700 border border-gray-600 rounded-lg p-4 flex items-start gap-3"
                      >
                        <FlagIcon className="h-5 w-5 text-gray-400 mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-white">{output.name}</h4>
                            <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                              {output.schema?.type || 'N/A'}
                            </span>
                          </div>
                          {output.description && (
                            <p className="text-sm text-gray-300">{output.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-400 mb-4">
                  Nenhuma estrutura de saída configurada.
                </p>
              )}
              {structuredOutputs.length > 0 && (
                <Button
                  onClick={() => {
                    setSelectedAssistantDetails(null);
                    navigate('/call-logs');
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white mb-2"
                >
                  <ChevronRightIcon className="h-4 w-4 mr-2" />
                  Ver Todos os Logs de Chamadas
                </Button>
              )}
              <Button
                onClick={() => {
                  setSelectedAssistantDetails(null);
                  navigate('/structured-outputs');
                }}
                variant="outline"
                className="w-full"
              >
                <PlusCircleIcon className="h-4 w-4 mr-2" />
                Adicionar mais Estruturas de Saída
              </Button>
            </div>
            <button
              onClick={() => {
                setSelectedAssistantDetails(null);
                setStructuredOutputs([]);
              }}
              className="mt-4 px-4 py-2 bg-gray-600 rounded-lg font-semibold hover:bg-gray-700"
            >
              Fechar
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}
